import {
  ALL_TOP_REVISIONS,
  CREATE_NEW_DONOR,
  CREATE_NEW_SIRE,
  DEACTIVATE_DONOR_SCHEDULES,
  DEACTIVATE_SO_SCHEDULE,
  DonorSchedule,
  EDIT_BULK_DONOR_SCHEDULES,
  GET_DONOR_SCHEDULES,
  GET_DONOR_SCHEDULES_BY_IDS,
  MASS_SET_FIELD,
  UPDATE_DS_TRANSACTION,
  UPDATE_SIRE,
  UPSERT_DONOR_SCHEDULES,
} from '@/rtk-query.gql';

import { enhancedApi } from '../api/baseApi';

// Define a type for the response structure
interface IDonorSchedulesResponse {
  donorSchedules: {
    items: DonorSchedule[];
  };
}

export const donorSchedulesApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getDonorSchedules: build.query<IDonorSchedulesResponse, unknown>({
      query: (variables) => ({
        document: GET_DONOR_SCHEDULES,
        variables,
      }),
      transformResponse: (response: IDonorSchedulesResponse) => {
        const deduplicated = Object.values(
          response.donorSchedules.items.reduce(
            (acc: Record<string, DonorSchedule>, schedule: DonorSchedule) => ({
              ...acc,
              [schedule.id]: schedule,
            }),
            {} as Record<string, DonorSchedule>,
          ),
        );

        return {
          ...response,
          donorSchedules: {
            ...response.donorSchedules,
            items: deduplicated,
          },
        };
      },
      providesTags: ['DonorSchedules'],
    }),
    getDonorSchedulesByIds: build.query<IDonorSchedulesResponse, unknown>({
      query: (variables) => ({
        document: GET_DONOR_SCHEDULES_BY_IDS,
        variables,
      }),
      transformResponse: (response: {
        donorSchedulesByIds: DonorSchedule[];
      }) => {
        const deduplicated = Object.values(
          response.donorSchedulesByIds.reduce(
            (acc: Record<string, DonorSchedule>, schedule: DonorSchedule) => ({
              ...acc,
              [schedule.id]: schedule,
            }),
            {} as Record<string, DonorSchedule>,
          ),
        );

        return {
          donorSchedules: {
            items: deduplicated,
          },
        };
      },
      providesTags: ['DonorSchedules'],
    }),
    editBulkDonorSchedules: build.mutation({
      query: ({ input, bulkUpdateIds }) => {
        const {
          aspirationSite,
          csr,
          donor,
          owner,
          stateCode,
          fertilizationSite,
          textingAccount,
          scheduleMaster,
          drug,
          drug2,
          soSchedule,
          shipmentGroup,
          implantTypeValue,
          semen,
          sire,
          semenTypeValue,
          semenLocation,
          semenOwner,
          backupSemen,
          backupSire,
          billingAccount,
          partnerships,
          annotation,
          ...filteredInput
        } = input;

        return {
          document: EDIT_BULK_DONOR_SCHEDULES,
          variables: {
            input: filteredInput,
            bulkUpdateIds,
            annotationNoteText: input.annotation?.noteText,
          },
        };
      },
      invalidatesTags: ['DonorSchedules', 'AllTopRevisions', 'Partnerships'],
    }),
    upsertDonorSchedules: build.mutation({
      query: ({ input }) => {
        const {
          aspirationSite,
          csr,
          donor,
          owner,
          stateCode,
          fertilizationSite,
          textingAccount,
          scheduleMaster,
          drug,
          drug2,
          soSchedule,
          shipmentGroup,
          implantTypeValue,
          semen,
          sire,
          semenTypeValue,
          semenLocation,
          semenOwner,
          backupSemen,
          backupSire,
          billingAccount,
          partnerships,
          annotation,
          createdBy,
          freezeMethod,
          ...filteredInput
        } = input;

        return {
          document: UPSERT_DONOR_SCHEDULES,
          variables: {
            input: filteredInput,
            annotationNoteText: input.annotation?.noteText,
          },
        };
      },
      invalidatesTags: ['DonorSchedules', 'AllTopRevisions', 'Partnerships'],
    }),
    deactivateDonorSchedules: build.mutation({
      query: (variables) => ({
        document: DEACTIVATE_DONOR_SCHEDULES,
        variables,
      }),
      invalidatesTags: ['DonorSchedules'],
    }),
    updateDonorSchedule: build.mutation({
      query: (variables) => ({
        document: UPDATE_DS_TRANSACTION,
        variables,
      }),
    }),
    createNewDonor: build.mutation({
      query: (variables) => ({
        document: CREATE_NEW_DONOR,
        variables,
      }),
    }),
    massSetField: build.mutation({
      query: (variables) => ({
        document: MASS_SET_FIELD,
        variables,
      }),
      invalidatesTags: ['DonorSchedules'],
    }),
    createNewSire: build.mutation({
      query: (variables) => ({
        document: CREATE_NEW_SIRE,
        variables,
      }),
      invalidatesTags: ['Sires'],
    }),
    updateSire: build.mutation({
      query: (variables) => ({
        document: UPDATE_SIRE,
        variables,
      }),
      invalidatesTags: ['Sires'],
    }),
    deactivateSoSchedule: build.mutation({
      query: (variables) => ({
        document: DEACTIVATE_SO_SCHEDULE,
        variables,
      }),
      invalidatesTags: ['DonorSchedules'],
    }),
    allTopRevisions: build.query({
      query: (variables) => ({
        document: ALL_TOP_REVISIONS,
        variables,
      }),
      providesTags: ['AllTopRevisions'],
    }),
  }),
});

export const {
  useGetDonorSchedulesQuery,
  useEditBulkDonorSchedulesMutation,
  useUpsertDonorSchedulesMutation,
  useUpdateDonorScheduleMutation,
  useCreateNewDonorMutation,
  useUpdateSireMutation,
  useCreateNewSireMutation,
  useLazyGetDonorSchedulesQuery,
  useDeactivateDonorSchedulesMutation,
  useMassSetFieldMutation,
  useDeactivateSoScheduleMutation,
  useGetDonorSchedulesByIdsQuery,
  useLazyGetDonorSchedulesByIdsQuery,
  useAllTopRevisionsQuery,
  useLazyAllTopRevisionsQuery,
} = donorSchedulesApi;
